import React, { useState, useEffect } from 'react';
import { legalService } from '../../services/api';

interface CalculationResult {
  earliestValidDate: string;
  noticePeriodDays: number;
  noticePeriodLabel: string;
  anchorDate: string;
  groundLabel: string;
  citation: string;
  isValid: boolean;
  warnings: Array<{ code: string; message: string }>;
}

interface Ground {
  code: string;
  label: string;
  periodMonths: number;
  periodDays: number;
  citation: string;
}

export default function NoticeDateCalculator() {
  const [expanded, setExpanded] = useState(false);
  const [rentPaymentDay, setRentPaymentDay] = useState('');
  const [groundType, setGroundType] = useState('8');
  const [intendedServiceDate, setIntendedServiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [freePeriodDays, setFreePeriodDays] = useState('0');
  const [freePeriodGranted, setFreePeriodGranted] = useState(false);
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [grounds, setGrounds] = useState<Ground[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    legalService.getNoticeGrounds().then((res: any) => {
      const data = res.data?.data || [];
      setGrounds(data);
    }).catch(() => {
      setGrounds([
        { code: '8', label: 'Ground 8: Serious Rent Arrears (8+ weeks)', periodMonths: 0, periodDays: 28, citation: 'Schedule 3, Paragraph 8' },
        { code: '10', label: 'Ground 10: Some Rent Arrears', periodMonths: 0, periodDays: 28, citation: 'Schedule 3, Paragraph 10' },
        { code: '11', label: 'Ground 11: Persistent Delay in Paying Rent', periodMonths: 0, periodDays: 28, citation: 'Schedule 3, Paragraph 11' },
        { code: '1', label: 'Ground 1: Occupation by Landlord/Family', periodMonths: 2, periodDays: 0, citation: 'Schedule 3, Paragraph 1' },
        { code: '1A', label: 'Ground 1A: Sale of Dwelling-house', periodMonths: 2, periodDays: 0, citation: 'Schedule 3, Paragraph 1A' },
        { code: '12', label: 'Ground 12: Breach of Tenancy Obligation', periodMonths: 0, periodDays: 14, citation: 'Schedule 3, Paragraph 12' },
        { code: '14', label: 'Ground 14: Nuisance or Annoyance', periodMonths: 0, periodDays: 0, citation: 'Schedule 3, Paragraph 14' },
      ]);
    });
  }, []);

  const handleCalculate = async () => {
    const day = parseInt(rentPaymentDay, 10);
    if (isNaN(day) || day < 1 || day > 28) {
      setError('Rent payment day must be between 1 and 28');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await legalService.calculateNoticeDate({
        rentPaymentDay: day,
        groundType,
        intendedServiceDate,
        freePeriodDays: parseInt(freePeriodDays, 10) || 0,
        freePeriodGranted,
      });
      setResult(res.data?.data || res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Calculation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResult = () => {
    if (!result) return;
    const text = [
      `Notice Date Calculation — ${result.isValid ? 'VALID' : 'INVALID'}`,
      `Earliest Valid Service Date: ${new Date(result.earliestValidDate).toLocaleDateString()}`,
      `Notice Period: ${result.noticePeriodLabel}`,
      `Anchor Date: ${new Date(result.anchorDate).toLocaleDateString()}`,
      `Legal Basis: ${result.citation}`,
      ...result.warnings.map(w => `Warning: ${w.message}`),
    ].join('\n');
    navigator.clipboard.writeText(text).catch(() => {});
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <i className="fas fa-calculator text-blue-600"></i>
          <span className="text-sm font-semibold text-gray-800">Notice Date Calculator</span>
        </div>
        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} text-gray-400 text-sm`}></i>
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          <p className="text-xs text-gray-500">
            Calculate the earliest legally valid date to serve a possession notice based on the tenant's rent payment date, notice type, and required notice period under the Renters' Rights Act 2025.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rent Payment Day of Month *</label>
              <input
                type="number"
                value={rentPaymentDay}
                onChange={(e) => setRentPaymentDay(e.target.value)}
                placeholder="e.g. 15"
                min="1"
                max="28"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-400 mt-0.5">Day of month (1–28) tenant pays rent</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Ground for Possession *</label>
              <select
                value={groundType}
                onChange={(e) => setGroundType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {grounds.map(g => (
                  <option key={g.code} value={g.code}>{g.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Intended Service Date</label>
              <input
                type="date"
                value={intendedServiceDate}
                onChange={(e) => setIntendedServiceDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={freePeriodGranted}
                  onChange={(e) => {
                    setFreePeriodGranted(e.target.checked);
                    if (!e.target.checked) setFreePeriodDays('0');
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                />
                <span className="text-xs text-gray-700">Free period (rent-free days) granted</span>
              </label>
            </div>
            {freePeriodGranted && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Free Period Days</label>
                <input
                  type="number"
                  value={freePeriodDays}
                  onChange={(e) => setFreePeriodDays(e.target.value)}
                  min="1"
                  max="90"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              <i className="fas fa-exclamation-circle mr-1"></i>{error}
            </div>
          )}

          <button
            onClick={handleCalculate}
            disabled={loading || !rentPaymentDay}
            className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? <><i className="fas fa-spinner fa-spin mr-2"></i>Calculating...</> : 'Calculate'}
          </button>

          {result && (
            <div className={`rounded-lg border p-4 ${result.isValid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <i className={`fas ${result.isValid ? 'fa-check-circle text-green-600' : 'fa-exclamation-triangle text-red-600'}`}></i>
                    <span className={`font-semibold text-sm ${result.isValid ? 'text-green-900' : 'text-red-900'}`}>
                      {result.isValid ? 'Valid Notice Date' : 'Invalid Notice Date'}
                    </span>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <p><span className="font-medium">Earliest Valid Service Date:</span> {new Date(result.earliestValidDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    <p><span className="font-medium">Notice Period:</span> {result.noticePeriodLabel}</p>
                    <p><span className="font-medium">Anchor Date:</span> {new Date(result.anchorDate).toLocaleDateString()}</p>
                    <p><span className="font-medium">Ground:</span> {result.groundLabel}</p>
                    <p><span className="font-medium">Citation:</span> {result.citation}</p>
                  </div>
                  {result.warnings.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {result.warnings.map((w, i) => (
                        <p key={i} className="text-xs text-amber-700 flex items-start gap-1">
                          <i className="fas fa-exclamation-circle mt-0.5"></i>
                          <span>{w.message}</span>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleCopyResult}
                  className="text-xs text-gray-500 hover:text-gray-700 p-1"
                  title="Copy to clipboard"
                >
                  <i className="fas fa-copy"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
